import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sun, Moon } from 'lucide-react';
import { VisibilityBars } from '@/components/calendar/VisibilityBars';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { DayData, getVisibilityRating } from '@/utils/calendarData';
import { format } from 'date-fns';
import { getMoonPhaseEmoji } from '@/utils/moonCalculations';
interface NightData {
  date: Date;
  dayData: DayData | null;
  rating: number;
}

interface TripCalendarTableProps {
  nightsData: NightData[];
}


export const TripCalendarTable: React.FC<TripCalendarTableProps> = ({ nightsData }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <TooltipProvider>
        <div className="space-y-3">
          {nightsData.map(({ date, dayData }) => {
            if (!dayData) return null;
            const rating = getVisibilityRating(dayData);

            return (
              <Card key={date.toISOString()} className="glass-card border-border/50 shadow-md">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {/* Date and Visibility */}
                    <div className="flex justify-between items-center">
                      <h3 className="font-semibold text-base">{format(date, 'EEE, MMM d')}</h3>
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground mb-1">Visibility</div>
                          <VisibilityBars rating={rating} />
                        </div>
                        <span className="text-2xl">{getMoonPhaseEmoji(dayData.moonPhase, dayData.moonPhaseCycle)}</span>
                      </div>
                    </div>
                    
                    {/* Sun Times */}
                    <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
                          <Sun className="w-4 h-4" />
                          <span className="text-xs font-medium">Sunrise</span>
                        </div>
                        <p className="text-sm font-mono">{dayData.sunrise}</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
                          <Sun className="w-4 h-4" />
                          <span className="text-xs font-medium">Sunset</span>
                        </div>
                        <p className="text-sm font-mono">{dayData.sunset}</p>
                      </div>
                    </div>

                    {/* Moon Times */}
                    <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 dark:bg-muted/30 rounded-lg">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-galaxy-blue mb-1">
                          <Moon className="w-4 h-4" />
                          <span className="text-xs font-medium">Moonrise</span>
                        </div>
                        <p className="text-sm font-mono">{dayData.moonrise}</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-galaxy-blue mb-1">
                          <Moon className="w-4 h-4" />
                          <span className="text-xs font-medium">Moonset</span>
                        </div>
                        <p className="text-sm font-mono">{dayData.moonset}</p>
                      </div>
                    </div>

                    {/* Galactic Core Times */}
                    <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-gold-foreground dark:text-gold-light mb-1">
                          <span className="text-sm">✨</span>
                          <span className="text-xs font-medium">GC Start</span>
                        </div>
                        <p className={`text-sm font-mono ${dayData.galacticCoreVisible ? "text-gold-foreground dark:text-gold-light font-medium" : "text-muted-foreground"}`}>
                          {dayData.galacticCoreStart}
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-gold-foreground dark:text-gold-light mb-1">
                          <span className="text-sm">✨</span>
                          <span className="text-xs font-medium">GC End</span>
                        </div>
                        <p className={`text-sm font-mono ${dayData.galacticCoreVisible ? "text-gold-foreground dark:text-gold-light font-medium" : "text-muted-foreground"}`}>
                          {dayData.galacticCoreEnd}
                        </p>
                      </div>
                    </div>

                    {/* Moon Phase Percentage */}
                    <div className="text-center p-2 bg-muted/50 rounded-lg">
                      <span className="text-xs text-muted-foreground">Moon Illumination: </span>
                      <span className="text-sm font-medium">{Math.round(dayData.moonPhase * 100)}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Card className="glass-card border-border/50 shadow-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 bg-secondary/20">
                  <TableHead className="text-foreground font-medium py-4 min-w-[100px] sticky left-0 bg-secondary/20 z-10">Date</TableHead>
                  <TableHead className="text-amber-600 font-medium min-w-[70px]">
                    <div className="flex items-center">
                      <Sun className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Sunrise</span>
                      <span className="sm:hidden">Rise</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-amber-600 font-medium min-w-[70px]">
                    <div className="flex items-center">
                      <Sun className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Sunset</span>
                      <span className="sm:hidden">Set</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[70px] hidden md:table-cell">
                    <div className="flex items-center">
                      <Moon className="w-3 h-3 mr-1" />
                      <span className="hidden lg:inline">Moonrise</span>
                      <span className="lg:hidden">Rise</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[70px] hidden md:table-cell">
                    <div className="flex items-center">
                      <Moon className="w-3 h-3 mr-1" />
                      <span className="hidden lg:inline">Moonset</span>
                      <span className="lg:hidden">Set</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-space-gray font-medium min-w-[80px] hidden lg:table-cell">
                    <div className="flex items-center">
                      <span className="mr-1">✨</span>
                      <span className="hidden xl:inline whitespace-normal">Galactic Core Start</span>
                      <span className="xl:hidden">GC Start</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-space-gray font-medium min-w-[80px] hidden lg:table-cell">
                    <div className="flex items-center">
                      <span className="mr-1">✨</span>
                      <span className="hidden xl:inline whitespace-normal">Galactic Core End</span>
                      <span className="xl:hidden">GC End</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-muted-foreground font-medium min-w-[50px] text-center hidden sm:table-cell">
                    <span className="hidden md:inline">Moon %</span>
                    <span className="md:hidden">%</span>
                  </TableHead>
                  <TableHead className="text-muted-foreground font-medium min-w-[50px] text-center">
                    <span className="hidden sm:inline">Phase</span>
                    <span className="sm:hidden">🌙</span>
                  </TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[80px] text-center sticky right-0 bg-secondary/20 z-10">
                    Visibility
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {nightsData.map(({ date, dayData }) => {
                  if (!dayData) return null;
                  const rating = getVisibilityRating(dayData);

                  return (
                    <TableRow key={date.toISOString()} className="border-border/30 hover:bg-secondary/20 transition-colors">
                      <TableCell className="text-foreground font-medium py-3 sticky left-0 bg-background z-10 border-r border-border/30">
                        <div className="text-sm">{format(date, 'EEE, MMM d')}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">{dayData.sunrise}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{dayData.sunset}</TableCell>
                      <TableCell className="text-muted-foreground text-xs hidden md:table-cell">{dayData.moonrise}</TableCell>
                      <TableCell className="text-muted-foreground text-xs hidden md:table-cell">{dayData.moonset}</TableCell>
                      <TableCell className={`${dayData.galacticCoreVisible ? "text-space-gray font-medium" : "text-muted-foreground"} text-xs hidden lg:table-cell`}>
                        {dayData.galacticCoreStart}
                      </TableCell>
                      <TableCell className={`${dayData.galacticCoreVisible ? "text-space-gray font-medium" : "text-muted-foreground"} text-xs hidden lg:table-cell`}>
                        {dayData.galacticCoreEnd}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-center text-xs hidden sm:table-cell">{Math.round(dayData.moonPhase * 100)}%</TableCell>
                      <TableCell className="text-xl text-center">
                        {getMoonPhaseEmoji(dayData.moonPhase, dayData.moonPhaseCycle)}
                      </TableCell>
                      <TableCell className="text-center sticky right-0 bg-background z-10 border-l border-border/30">
                        <VisibilityBars rating={rating} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
